import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronDown, Search, ShieldCheck, X } from 'lucide-react';
import { api, getApiError } from '@/services/api';
import { EmptyState, LoadingState, PageHeader } from '@/components/ui';
import { localeFor } from '@/components/reports/chartTheme';
import { AdminPagination } from './AdminPagination';

interface AuditLog {
  id: string;
  timestamp: string;
  userId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  details: Record<string, unknown> | null;
  user?: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  } | null;
}

interface AuditLogsResponse {
  logs: AuditLog[];
  pagination: { page: number; limit?: number; total?: number; totalPages: number };
}

const ACTIONS = [
  'LOGIN',
  'LOGIN_FAILED',
  'LOGOUT',
  'CREATE',
  'READ',
  'UPDATE',
  'DELETE',
  'SIGN',
  'FULFILL',
  'CANCEL',
  'SHARE',
  'REVOKE_SHARE',
] as const;

const ENTITY_TYPES = ['Session', 'User', 'Patient', 'Prescription', 'Drug'] as const;

const actionBadges: Record<string, string> = {
  LOGIN: 'badge bg-navy-50 text-navy-700 ring-navy-600/20',
  LOGIN_FAILED: 'badge-danger',
  LOGOUT: 'badge-gray',
  CREATE: 'badge-success',
  READ: 'badge-gray',
  UPDATE: 'badge-primary',
  DELETE: 'badge-danger',
  SIGN: 'badge bg-violet-50 text-violet-700 ring-violet-600/20',
  FULFILL: 'badge-success',
  CANCEL: 'badge-danger',
  SHARE: 'badge-primary',
  REVOKE_SHARE: 'badge-warning',
};

export function AuditLogs() {
  const { t, i18n } = useTranslation();
  const locale = localeFor(i18n.language);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userSearchInput, setUserSearchInput] = useState('');

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['audit', 'logs', page, actionFilter, entityTypeFilter, userSearch],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (actionFilter) params.set('action', actionFilter);
      if (entityTypeFilter) params.set('entityType', entityTypeFilter);
      if (userSearch) params.set('userSearch', userSearch);
      const response = await api.get(`/audit/logs?${params}`);
      return response.data as AuditLogsResponse;
    },
    placeholderData: keepPreviousData,
  });

  const handleFilterChange =
    (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      setter(e.target.value);
      setPage(1);
    };

  const handleUserSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUserSearch(userSearchInput.trim());
    setPage(1);
  };

  const handleClearUserSearch = () => {
    setUserSearchInput('');
    setUserSearch('');
    setPage(1);
  };

  const actionLabel = (action: string) => t(`admin.audit.actions.${action}`, { defaultValue: action });
  const entityLabel = (type: string) => t(`admin.audit.entities.${type}`, { defaultValue: type });

  const logs = data?.logs ?? [];
  const hasFilters = Boolean(actionFilter || entityTypeFilter || userSearch);

  return (
    <div>
      <PageHeader title={t('nav.audit')} description={t('admin.audit.description')} />

      {/* Filters */}
      <div className="card mb-6 p-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label htmlFor="audit-action" className="label">
              {t('admin.audit.action')}
            </label>
            <select
              id="audit-action"
              className="input"
              value={actionFilter}
              onChange={handleFilterChange(setActionFilter)}
            >
              <option value="">{t('admin.audit.allActions')}</option>
              {ACTIONS.map((action) => (
                <option key={action} value={action}>
                  {actionLabel(action)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="audit-entity" className="label">
              {t('admin.audit.entityType')}
            </label>
            <select
              id="audit-entity"
              className="input"
              value={entityTypeFilter}
              onChange={handleFilterChange(setEntityTypeFilter)}
            >
              <option value="">{t('admin.audit.allEntities')}</option>
              {ENTITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {entityLabel(type)}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <label htmlFor="audit-user" className="label">
              {t('admin.audit.user')}
            </label>
            <form onSubmit={handleUserSearchSubmit} className="flex gap-2">
              <input
                id="audit-user"
                type="search"
                className="input min-w-0 flex-1"
                placeholder={t('admin.audit.userPlaceholder')}
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
              />
              {userSearch && userSearch === userSearchInput.trim() ? (
                <button
                  type="button"
                  onClick={handleClearUserSearch}
                  className="btn-secondary px-3"
                  aria-label={t('admin.audit.clearSearch')}
                  title={t('admin.audit.clearSearch')}
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn-primary px-3"
                  aria-label={t('common.search')}
                  title={t('common.search')}
                >
                  <Search className="h-4 w-4" />
                </button>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Logs */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <EmptyState
            icon={AlertCircle}
            title={t('admin.loadError')}
            description={getApiError(error).message}
            action={
              <button type="button" className="btn-secondary" onClick={() => refetch()}>
                {t('admin.retry')}
              </button>
            }
          />
        ) : logs.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title={t('common.noResults')}
            description={hasFilters ? t('admin.audit.emptyFiltered') : t('admin.audit.empty')}
          />
        ) : (
          <>
            <ul className={`divide-y divide-gray-100 transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
              {logs.map((log) => {
                const hasDetails =
                  log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0;
                return (
                  <li key={log.id} className="px-4 py-3 sm:px-5">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <div className="flex min-w-0 items-start gap-3">
                        <span className={`${actionBadges[log.action] || 'badge-gray'} mt-0.5 shrink-0`}>
                          {actionLabel(log.action)}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {entityLabel(log.entityType)}
                            {log.entityId && (
                              <span className="ms-1.5 font-mono text-xs text-gray-500" title={log.entityId}>
                                #{log.entityId.slice(-8)}
                              </span>
                            )}
                          </p>
                          <p className="mt-0.5 break-words text-xs text-gray-500">
                            {log.user
                              ? t('admin.audit.by', {
                                  name: `${log.user.firstName} ${log.user.lastName}`,
                                  email: log.user.email,
                                })
                              : t('admin.audit.systemOrAnonymous')}
                          </p>
                        </div>
                      </div>
                      <time
                        dateTime={log.timestamp}
                        className="shrink-0 ps-0 text-xs tabular-nums text-gray-500 sm:ps-4 sm:text-end"
                      >
                        {new Date(log.timestamp).toLocaleString(locale, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </time>
                    </div>
                    {hasDetails && (
                      <details className="group mt-2">
                        <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 [&::-webkit-details-marker]:hidden">
                          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                          {t('admin.audit.details')}
                        </summary>
                        <pre
                          dir="ltr"
                          className="mt-2 max-h-72 overflow-auto rounded-lg border border-gray-100 bg-gray-50 p-3 text-start text-xs text-gray-700"
                        >
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </li>
                );
              })}
            </ul>
            {data?.pagination && (
              <AdminPagination
                page={page}
                totalPages={data.pagination.totalPages}
                total={data.pagination.total}
                onPageChange={setPage}
                disabled={isFetching}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
